import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { Zap, Search, X } from 'lucide-react';

interface Tooltip {
  id: string;
  text: string;
  /** 目标元素选择器 */
  selector: string;
  /** 默认位置：top-left / top-right / bottom */
  position: 'top-left' | 'top-right' | 'bottom';
  /** 显示后 3 秒自动消失 */
  showUntil: number;
}

interface ContextualTooltipsProps {
  isZh: boolean;
  enabled: boolean;
}

const TOOLTIP_DURATION_MS = 5000;
const STORAGE_KEY = 'xf_context_tooltips_seen';

/**
 * 教程完成后，在关键 UI 元素旁显示轻量引导提示
 * 首次显示，3天后自动过期不再打扰
 */
export function ContextualTooltips({ isZh, enabled }: ContextualTooltipsProps) {
  const [tooltips, setTooltips] = useState<Tooltip[]>([]);
  const [dismissed, setDismissed] = useState<Set<string>>(new Set());

  const ui = useCallback((zh: string, en: string) => isZh ? zh : en, [isZh]);

  useEffect(() => {
    if (!enabled) return;

    // 检查是否在 3 天有效期内
    try {
      const seen = localStorage.getItem(STORAGE_KEY);
      if (seen) {
        const expiry = parseInt(seen, 10);
        if (Date.now() > expiry) return; // 已过期
      }
    } catch { return; }

    const showUntil = Date.now() + TOOLTIP_DURATION_MS;

    // 定义 3 个引导提示
    const items: Tooltip[] = [
      {
        id: 'dock',
        text: ui('长按右下角小枫按钮试试快捷菜单', 'Long-press the button for quick actions'),
        selector: '[data-drag-handle]',
        position: 'top-left',
        showUntil,
      },
      {
        id: 'search',
        text: ui('试试在搜索栏输入功能名，回车直接打开', 'Try searching a feature name and press Enter'),
        selector: '[data-quick-search]',
        position: 'top-right',
        showUntil,
      },
      {
        id: 'chat',
        text: ui('点我开启对话，用小枫帮你做事', 'Click me to chat and let me help you'),
        selector: '[data-chat-orb]',
        position: 'bottom',
        showUntil,
      },
    ];

    // 只对存在的 DOM 元素添加 tooltip
    const valid = items.filter(item => document.querySelector(item.selector));
    if (valid.length === 0) return;

    setTooltips(valid);
    // 写入 localStorage（3天后过期）
    try { localStorage.setItem(STORAGE_KEY, String(Date.now() + 3 * 24 * 60 * 60 * 1000)); } catch {}

    // 自动消失
    const timer = setTimeout(() => setTooltips([]), TOOLTIP_DURATION_MS);
    return () => clearTimeout(timer);
  }, [enabled, ui]);

  const dismiss = (id: string) => {
    setDismissed(prev => new Set(prev).add(id));
    if (dismissed.size + 1 >= tooltips.length) setTooltips([]);
  };

  return (
    <AnimatePresence>
      {tooltips
        .filter(t => !dismissed.has(t.id))
        .map(tooltip => {
          const el = document.querySelector(tooltip.selector);
          if (!el) return null;
          let rect: DOMRect;
          try { rect = el.getBoundingClientRect(); } catch { return null; }

          let posStyle: React.CSSProperties = {};
          if (tooltip.position === 'top-left') {
            posStyle = { bottom: window.innerHeight - rect.top + 12, right: window.innerWidth - rect.left, left: 'auto', top: 'auto' };
          } else if (tooltip.position === 'top-right') {
            posStyle = { bottom: window.innerHeight - rect.top + 12, left: rect.right, top: 'auto', right: 'auto' };
          } else {
            posStyle = { top: rect.bottom + 12, left: rect.left + rect.width / 2, transform: 'translateX(-50%)' };
          }

          return (
            <motion.div
              key={tooltip.id}
              initial={{ opacity: 0, y: 4 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -4 }}
              className="fixed z-[210] flex items-center gap-2 rounded-2xl border border-celestial-saturn/30 bg-celestial-deep/90 backdrop-blur-xl px-4 py-3 shadow-2xl pointer-events-auto max-w-[260px]"
              style={posStyle}
            >
              <span className="shrink-0">{tooltip.id === 'search' ? <Search size={14} className="text-celestial-saturn" /> : <Zap size={14} className="text-celestial-saturn" />}</span>
              <span className="text-xs font-bold text-white/80 leading-relaxed">{tooltip.text}</span>
              <button onClick={() => dismiss(tooltip.id)} className="shrink-0 text-white/25 hover:text-white/60">
                <X size={12} />
              </button>
            </motion.div>
          );
        })}
    </AnimatePresence>
  );
}
